import { signOutWipe } from '@/features/offline/swBridge';
import { SW_DB_NAME } from '@/sw/db';
import { registerSW } from 'virtual:pwa-register';

/**
 * The offline service worker is OPT-IN per device. The localStorage flag is
 * TRI-STATE and the distinction is load-bearing:
 *
 *   'true'  — user installed here (or auto-install ran, see below)
 *   'false' — user explicitly uninstalled here; NEVER auto-install again
 *   absent  — no choice made on this device → the per-user cross-client
 *             default (me.settings.offline_sync_default_enabled) applies,
 *             letting key users get offline sync installed remotely
 *             (OfflineManager runs the auto-install)
 *
 * registerType is 'prompt': a new SW version installs but stays waiting until
 * a window posts SKIP_WAITING (queue-gated activation).
 */

const SW_ENABLED_KEY = 'certhon:sw-enabled';

/** Fired on window whenever the enabled flag flips (install/uninstall). */
export const SW_ENABLED_EVENT = 'sw-enabled-changed';

export function isServiceWorkerEnabled(): boolean {
  try {
    return (
      'serviceWorker' in navigator &&
      localStorage.getItem(SW_ENABLED_KEY) === 'true'
    );
  } catch {
    return false;
  }
}

/**
 * Whether the user made an explicit install/uninstall choice on this device.
 * When true, the server-side default must never override it.
 */
export function hasServiceWorkerChoice(): boolean {
  try {
    return localStorage.getItem(SW_ENABLED_KEY) !== null;
  } catch {
    return true; // no storage → treat as decided, never auto-install
  }
}

function setEnabledFlag(enabled: boolean): void {
  localStorage.setItem(SW_ENABLED_KEY, String(enabled));
  window.dispatchEvent(new Event(SW_ENABLED_EVENT));
}

/** Boot path (main.tsx): registers only when previously installed. */
export function registerServiceWorker(): void {
  if (!isServiceWorkerEnabled()) {
    return;
  }
  registerSW({ immediate: true });
}

/** Install from the sync status panel: persist the opt-in and register. */
export async function installServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported in this browser');
  }
  setEnabledFlag(true);
  registerSW({ immediate: true });
  await navigator.serviceWorker.ready;
}

/**
 * Uninstall: wipe all offline data, unregister every registration, and
 * reload — an unregistered SW keeps controlling open pages until the next
 * navigation.
 *
 * The SW-side sign-out wipe is best-effort (the SW may be unreachable); the
 * window then cleans CacheStorage and the SW database DIRECTLY, so uninstall
 * never strands data. This is also what removes the workbox app-shell
 * precache: the sign-out wipe deliberately keeps it (the SW stays installed
 * there), and after unregister no SW activate will ever clean it up.
 */
export async function uninstallServiceWorker(): Promise<void> {
  setEnabledFlag(false);
  try {
    await signOutWipe();
  } catch {
    // SW unreachable — the direct cleanup below covers it
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((r) => r.unregister()));
  try {
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    indexedDB.deleteDatabase(SW_DB_NAME);
  } catch {
    // storage APIs unavailable — nothing more to clean
  }
  window.location.reload();
}
